import { useCallback, useEffect, useRef, useState } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { useSessionLease, isSessionStaleError } from '../context/SessionLeaseProvider'
import { OwnerCard } from '../kit/OwnerCard'
import {
  getOwnerHomeSnapshot,
  type OwnerHomeSnapshot,
} from '../../../services/ownerConsoleService'
import { navigateOwner, type OwnerRoute } from '../ownerRouter'
import './ownerHome.css'

export type OwnerHomePanelProps = {
  onNavigate?: (target: OwnerRoute | string) => void
  onLeaseMinutesLoaded?: (mins: number) => void
  onLoadingChange?: (loading: boolean) => void
  onSnapshotLoaded?: (snapshot: OwnerHomeSnapshot) => void
  refreshNonce?: number
}

const formatAsOfTime = (isoString?: string): string => {
  if (!isoString) return ''
  const d = new Date(isoString)
  if (isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d)
}

const formatActionTime = (ts: unknown): string => {
  if (!ts) return ''
  let date: Date | null = null
  if (typeof ts === 'string' || typeof ts === 'number') {
    date = new Date(ts)
  } else if (typeof ts === 'object' && ts !== null) {
    const obj = ts as { iso?: string; seconds?: number }
    if (obj.iso) date = new Date(obj.iso)
    else if (typeof obj.seconds === 'number') date = new Date(obj.seconds * 1000)
  }
  if (!date || isNaN(date.getTime())) return ''
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

export function OwnerHomePanel({
  onNavigate,
  onLeaseMinutesLoaded,
  onLoadingChange,
  onSnapshotLoaded,
  refreshNonce = 0,
}: OwnerHomePanelProps) {
  const { t } = useNls()
  const { triggerSessionStale } = useSessionLease()
  const [snapshot, setSnapshot] = useState<OwnerHomeSnapshot | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const onLeaseMinutesLoadedRef = useRef(onLeaseMinutesLoaded)
  const onLoadingChangeRef = useRef(onLoadingChange)
  const onSnapshotLoadedRef = useRef(onSnapshotLoaded)
  const triggerSessionStaleRef = useRef(triggerSessionStale)

  useEffect(() => {
    onLeaseMinutesLoadedRef.current = onLeaseMinutesLoaded
    onLoadingChangeRef.current = onLoadingChange
    onSnapshotLoadedRef.current = onSnapshotLoaded
    triggerSessionStaleRef.current = triggerSessionStale
  })

  const handleNavigate = useCallback(
    (target: OwnerRoute | string) => {
      if (onNavigate) {
        onNavigate(target)
      } else {
        navigateOwner(target)
      }
    },
    [onNavigate],
  )

  useEffect(() => {
    let cancelled = false

    const fetchSnapshot = async () => {
      setLoading(true)
      setError(null)
      onLoadingChangeRef.current?.(true)

      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
      try {
        const data = await getOwnerHomeSnapshot(timeZone)
        if (cancelled) return
        setSnapshot(data)
        setLoading(false)
        onLoadingChangeRef.current?.(false)
        if (typeof data.leaseMinutes === 'number') {
          onLeaseMinutesLoadedRef.current?.(data.leaseMinutes)
        }
        onSnapshotLoadedRef.current?.(data)
      } catch (err: unknown) {
        if (cancelled) return
        setLoading(false)
        onLoadingChangeRef.current?.(false)
        if (isSessionStaleError(err)) {
          triggerSessionStaleRef.current?.()
        }
        setError(t('NLSOwnerConsoleHomeError'))
      }
    }

    void Promise.resolve().then(fetchSnapshot)

    return () => {
      cancelled = true
    }
  }, [refreshNonce, t])

  if (loading && !snapshot) {
    return (
      <section className="ownerHomePanel" data-testid="owner-home-panel">
        <div className="ownerHomeLoading">{t('NLSOwnerConsoleLoadingData')}</div>
      </section>
    )
  }

  if (error && !snapshot) {
    return (
      <section className="ownerHomePanel" data-testid="owner-home-panel">
        <div className="ownerHomeError" role="alert">{error}</div>
      </section>
    )
  }

  if (!snapshot) return null

  const asOfFormatted = formatAsOfTime(snapshot.asOf)
  const ticketsSec = snapshot.tickets
  const signupsSec = snapshot.signups
  const burnSec = snapshot.burn
  const moneySec = snapshot.money
  const riskSec = snapshot.risk
  const totalsSec = snapshot.totals
  const recentActionsSec = snapshot.recentActions
  const recentTicketsSec = snapshot.recentTickets

  return (
    <section className="ownerHomePanel" data-testid="owner-home-panel">
      <div className="ownerHomeHeader">
        <h2 className="ownerHomeTitle">{t('NLSOwnerConsoleNavHome')}</h2>
        {asOfFormatted && (
          <span className="ownerHomeAsOf" data-testid="owner-home-as-of">
            {t('NLSOwnerConsoleHomeAsOf', { time: asOfFormatted })}
          </span>
        )}
      </div>

      {totalsSec && (
        <div className="ownerHomeTotalsLine" data-testid="owner-home-totals">
          {totalsSec.state === 'unavailable' ? (
            <span className="ownerHomeUnavailable">
              {t('NLSOwnerConsoleHomeSectionUnavailable')}
            </span>
          ) : (
            <span>
              {t('NLSOwnerConsoleHomeTotals', {
                workspaces: (totalsSec.workspaceCount ?? 0).toLocaleString(),
                users: (totalsSec.userCount ?? 0).toLocaleString(),
              })}
            </span>
          )}
        </div>
      )}

      <div className="ownerHomeGrid">
        {ticketsSec && (
          ticketsSec.state === 'unavailable' ? (
            <OwnerCard
              title={t('NLSOwnerConsoleHomeTicketsTitle')}
              className="ownerHomeTile"
            >
              <p className="ownerHomeUnavailable">
                {t('NLSOwnerConsoleHomeSectionUnavailable')}
              </p>
            </OwnerCard>
          ) : (() => {
            const hasBreach = (ticketsSec.breachedCount ?? 0) > 0 || Boolean(ticketsSec.hasBreach)
            const countLabel = ticketsSec.truncated ? '200+' : String(ticketsSec.count ?? 0)
            const target = 'inbox?savedView=awaiting-me'
            return (
              <OwnerCard
                title={t('NLSOwnerConsoleHomeTicketsTitle')}
                className={`ownerHomeTile ${hasBreach ? 'ownerHomeTile--danger' : ''}`}
                primaryAction={
                  <button
                    type="button"
                    className="ownerHomeTileLink"
                    data-testid="link-home-tickets"
                    onClick={() => handleNavigate(target)}
                    aria-label={t('NLSOwnerConsoleHomeTicketsTitle')}
                  >
                    {t('NLSOwnerConsoleHomeViewAction')}
                  </button>
                }
              >
                <div
                  className="ownerHomeTileBody"
                  onClick={() => handleNavigate(target)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleNavigate(target)
                    }
                  }}
                >
                  <div
                    className={`ownerHomeTileValue ${hasBreach ? 'ownerHomeTileValue--danger' : ''}`}
                    data-tone={hasBreach ? 'danger' : 'neutral'}
                  >
                    {countLabel}
                  </div>
                  {hasBreach && (
                    <div className="ownerHomeTileSubtitle ownerHomeTileSubtitle--danger">
                      {t('NLSOwnerConsoleHomeBreachedCount', {
                        count: ticketsSec.breachedCount ?? 0,
                      })}
                    </div>
                  )}
                </div>
              </OwnerCard>
            )
          })()
        )}

        {signupsSec && (
          signupsSec.state === 'unavailable' ? (
            <OwnerCard
              title={t('NLSOwnerConsoleHomeSignupsTitle')}
              className="ownerHomeTile"
            >
              <p className="ownerHomeUnavailable">
                {t('NLSOwnerConsoleHomeSectionUnavailable')}
              </p>
            </OwnerCard>
          ) : (() => {
            const todayCount = signupsSec.todayCount ?? 0
            const delta = signupsSec.deltaVsAverage ?? 0
            const deltaPrefix = delta > 0 ? `+${delta}` : String(delta)
            const target = 'people?savedView=new-signups'
            return (
              <OwnerCard
                title={t('NLSOwnerConsoleHomeSignupsTitle')}
                className="ownerHomeTile"
                primaryAction={
                  <button
                    type="button"
                    className="ownerHomeTileLink"
                    data-testid="link-home-signups"
                    onClick={() => handleNavigate(target)}
                    aria-label={t('NLSOwnerConsoleHomeSignupsTitle')}
                  >
                    {t('NLSOwnerConsoleHomeViewAction')}
                  </button>
                }
              >
                <div
                  className="ownerHomeTileBody"
                  onClick={() => handleNavigate(target)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleNavigate(target)
                    }
                  }}
                >
                  <div className="ownerHomeTileValue">{todayCount.toLocaleString()}</div>
                  <div className="ownerHomeTileSubtitle">
                    {t('NLSOwnerConsoleHomeDeltaVsAvg', { delta: deltaPrefix })}
                  </div>
                </div>
              </OwnerCard>
            )
          })()
        )}

        {burnSec && (
          burnSec.state === 'unavailable' ? (
            <OwnerCard
              title={t('NLSOwnerConsoleHomeBurnTitle')}
              className="ownerHomeTile"
            >
              <p className="ownerHomeUnavailable">
                {t('NLSOwnerConsoleHomeSectionUnavailable')}
              </p>
            </OwnerCard>
          ) : (() => {
            const burnToday = burnSec.burnToday ?? 0
            const target = 'workspaces'
            return (
              <OwnerCard
                title={t('NLSOwnerConsoleHomeBurnTitle')}
                className="ownerHomeTile"
                primaryAction={
                  <button
                    type="button"
                    className="ownerHomeTileLink"
                    data-testid="link-home-burn"
                    onClick={() => handleNavigate(target)}
                    aria-label={t('NLSOwnerConsoleHomeBurnTitle')}
                  >
                    {t('NLSOwnerConsoleHomeViewAction')}
                  </button>
                }
              >
                <div
                  className="ownerHomeTileBody"
                  onClick={() => handleNavigate(target)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleNavigate(target)
                    }
                  }}
                >
                  <div className="ownerHomeTileValue">{burnToday.toLocaleString()}</div>
                </div>
              </OwnerCard>
            )
          })()
        )}

        {moneySec && (
          moneySec.state === 'unavailable' ? (
            <OwnerCard
              title={t('NLSOwnerConsoleHomeMoneyTitle')}
              className="ownerHomeTile"
            >
              <p className="ownerHomeUnavailable">
                {t('NLSOwnerConsoleHomeSectionUnavailable')}
              </p>
            </OwnerCard>
          ) : (() => {
            const mrr = moneySec.mrr ?? 0
            const burnMonth = moneySec.burnMonth
            const target = 'revenue'
            return (
              <OwnerCard
                title={t('NLSOwnerConsoleHomeMoneyTitle')}
                className="ownerHomeTile"
                primaryAction={
                  <button
                    type="button"
                    className="ownerHomeTileLink"
                    data-testid="link-home-money"
                    onClick={() => handleNavigate(target)}
                    aria-label={t('NLSOwnerConsoleHomeMoneyTitle')}
                  >
                    {t('NLSOwnerConsoleHomeViewAction')}
                  </button>
                }
              >
                <div
                  className="ownerHomeTileBody"
                  onClick={() => handleNavigate(target)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleNavigate(target)
                    }
                  }}
                >
                  <div className="ownerHomeMoneyItem">
                    <div className="ownerHomeMoneyHeader">
                      <span className="ownerHomeMoneyLabel">{t('NLSOwnerConsoleHomeMrrLabel')}</span>
                      <span className="ownerChip ownerChip--modeled">
                        {t('NLSOwnerConsoleHomeModeledChip')}
                      </span>
                    </div>
                    <div className="ownerHomeTileValue">₹{mrr.toLocaleString('en-IN')}</div>
                  </div>
                  <div className="ownerHomeMoneyItem">
                    <div className="ownerHomeMoneyHeader">
                      <span className="ownerHomeMoneyLabel">
                        {t('NLSOwnerConsoleHomeBurnMonthLabel')}
                      </span>
                      {burnMonth !== undefined && (
                        <span className="ownerChip ownerChip--live">
                          {t('NLSOwnerConsoleHomeLiveChip')}
                        </span>
                      )}
                    </div>
                    <div className="ownerHomeTileSubvalue">
                      {burnMonth !== undefined ? (
                        burnMonth.toLocaleString()
                      ) : (
                        `— ${t('NLSOwnerConsoleHomeSectionUnavailable')}`
                      )}
                    </div>
                  </div>
                </div>
              </OwnerCard>
            )
          })()
        )}

        {riskSec && (
          riskSec.state === 'unavailable' ? (
            <OwnerCard
              title={t('NLSOwnerConsoleHomeRiskTitle')}
              className="ownerHomeTile"
            >
              <p className="ownerHomeUnavailable">
                {t('NLSOwnerConsoleHomeSectionUnavailable')}
              </p>
            </OwnerCard>
          ) : (() => {
            const openRisks = riskSec.openRiskCount ?? 0
            const failedAutomations = riskSec.failedAutomationCount ?? 0
            const totalRisk = riskSec.totalRiskCount ?? (openRisks + failedAutomations)
            const target = 'ops'
            return (
              <OwnerCard
                title={t('NLSOwnerConsoleHomeRiskTitle')}
                className={`ownerHomeTile ${totalRisk > 0 ? 'ownerHomeTile--warn' : ''}`}
                primaryAction={
                  <button
                    type="button"
                    className="ownerHomeTileLink"
                    data-testid="link-home-risk"
                    onClick={() => handleNavigate(target)}
                    aria-label={t('NLSOwnerConsoleHomeRiskTitle')}
                  >
                    {t('NLSOwnerConsoleHomeViewAction')}
                  </button>
                }
              >
                <div
                  className="ownerHomeTileBody"
                  onClick={() => handleNavigate(target)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleNavigate(target)
                    }
                  }}
                >
                  <div className={`ownerHomeTileValue ${totalRisk > 0 ? 'ownerHomeTileValue--warn' : ''}`}>
                    {totalRisk}
                  </div>
                  <div className="ownerHomeTileSubtitle">
                    {t('NLSOwnerConsoleHomeOpenRisks', { count: openRisks })},{' '}
                    {t('NLSOwnerConsoleHomeFailedAutomations', { count: failedAutomations })}
                  </div>
                </div>
              </OwnerCard>
            )
          })()
        )}
      </div>

      {(recentActionsSec || recentTicketsSec) && (
        <div className="ownerHomeRecentGrid">
          {recentActionsSec && (
            recentActionsSec.state === 'unavailable' ? (
              <OwnerCard
                title={t('NLSOwnerConsoleHomeRecentActionsTitle')}
                className="ownerHomeListCard"
              >
                <p className="ownerHomeUnavailable">
                  {t('NLSOwnerConsoleHomeSectionUnavailable')}
                </p>
              </OwnerCard>
            ) : (() => {
              const actions = (recentActionsSec.actions ?? []).slice(0, 5)
              return (
                <OwnerCard
                  title={t('NLSOwnerConsoleHomeRecentActionsTitle')}
                  className="ownerHomeListCard"
                  primaryAction={
                    <button
                      type="button"
                      className="ownerHomeTileLink"
                      data-testid="link-home-actions-view"
                      onClick={() => handleNavigate('activity')}
                      aria-label={t('NLSOwnerConsoleHomeRecentActionsTitle')}
                    >
                      {t('NLSOwnerConsoleHomeViewAction')}
                    </button>
                  }
                >
                  {actions.length === 0 ? (
                    <p className="ownerHomeEmpty">{t('NLSOwnerConsoleHomeNoActions')}</p>
                  ) : (
                    <ul className="ownerHomeList">
                      {actions.map((action) => {
                        const target = `activity/${encodeURIComponent(action.id)}`
                        return (
                          <li
                            key={action.id}
                            className="ownerHomeListItem"
                            role="button"
                            tabIndex={0}
                            onClick={() => handleNavigate(target)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                handleNavigate(target)
                              }
                            }}
                          >
                            <div className="ownerHomeListItemMain">
                              <span className="ownerHomeListItemVerb">{action.verb || action.kind}</span>
                              <span className="ownerHomeListItemMeta">
                                {action.actor?.label || action.actor?.id} · {action.outcome}
                              </span>
                            </div>
                            {action.createdAt && (
                              <span className="ownerHomeListItemTime">
                                {formatActionTime(action.createdAt)}
                              </span>
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </OwnerCard>
              )
            })()
          )}

          {recentTicketsSec && (
            recentTicketsSec.state === 'unavailable' ? (
              <OwnerCard
                title={t('NLSOwnerConsoleHomeRecentTicketsTitle')}
                className="ownerHomeListCard"
              >
                <p className="ownerHomeUnavailable">
                  {t('NLSOwnerConsoleHomeSectionUnavailable')}
                </p>
              </OwnerCard>
            ) : (() => {
              const tickets = (recentTicketsSec.tickets ?? []).slice(0, 5)
              return (
                <OwnerCard
                  title={t('NLSOwnerConsoleHomeRecentTicketsTitle')}
                  className="ownerHomeListCard"
                  primaryAction={
                    <button
                      type="button"
                      className="ownerHomeTileLink"
                      data-testid="link-home-tickets-view"
                      onClick={() => handleNavigate('inbox')}
                      aria-label={t('NLSOwnerConsoleHomeRecentTicketsTitle')}
                    >
                      {t('NLSOwnerConsoleHomeViewAction')}
                    </button>
                  }
                >
                  {tickets.length === 0 ? (
                    <p className="ownerHomeEmpty">{t('NLSOwnerConsoleHomeNoTickets')}</p>
                  ) : (
                    <ul className="ownerHomeList">
                      {tickets.map((ticket) => {
                        const target = `inbox/${encodeURIComponent(ticket.id)}`
                        return (
                          <li
                            key={ticket.id}
                            className="ownerHomeListItem"
                            role="button"
                            tabIndex={0}
                            onClick={() => handleNavigate(target)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                handleNavigate(target)
                              }
                            }}
                          >
                            <div className="ownerHomeListItemMain">
                              <span className="ownerHomeListItemTitle">{ticket.title}</span>
                              <span className="ownerHomeListItemMeta">
                                {ticket.status} · {ticket.priority}
                              </span>
                            </div>
                            {ticket.updatedAt && (
                              <span className="ownerHomeListItemTime">
                                {new Date(ticket.updatedAt).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </span>
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </OwnerCard>
              )
            })()
          )}
        </div>
      )}
    </section>
  )
}
